import React, { useEffect } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { getOnboardingStepIndex, getOnboardingStepIndexFromPath, ONBOARDING_STEP_COUNT } from "./onboardingDraft";
import { useOnboardingStepActions } from "./OnboardingStepActions";
import "./OnboardingWizard.css";

export function OnboardingWizardChrome({ stepIndex, onBack }) {
  const currentStep = stepIndex + 1;
  const progress = (currentStep / ONBOARDING_STEP_COUNT) * 100;

  return (
    <>
      <header className="onboarding-wizard__header">
        <button
          type="button"
          className="onboarding-wizard__back"
          onClick={onBack}
          aria-label="Go back"
        >
          <FiArrowLeft aria-hidden />
        </button>
        <div className="onboarding-wizard__progress-copy">
          <span className="onboarding-wizard__step-label">
            Step {currentStep} of {ONBOARDING_STEP_COUNT}
          </span>
        </div>
      </header>

      <div className="onboarding-wizard__progress" aria-hidden>
        <div
          className="onboarding-wizard__progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
    </>
  );
}

export function OnboardingWizardFooter() {
  const { actions } = useOnboardingStepActions();

  return (
    <footer className="onboarding-wizard__footer onboarding-wizard__footer--pinned">
      {actions.footer}
      {actions.showSave ? (
        <div className="onboarding-wizard__footer-actions">
          <button
            type="button"
            className="onboarding-wizard__continue btn-secondary"
            onClick={() => actions.onContinue?.()}
            disabled={actions.continueDisabled}
          >
            {actions.continueLabel}
          </button>
          <button
            type="button"
            className="onboarding-wizard__save btn-primary"
            onClick={() => actions.onSave?.()}
            disabled={actions.saveDisabled}
          >
            {actions.saveLabel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="onboarding-wizard__continue btn-primary"
          onClick={() => actions.onContinue?.()}
          disabled={actions.continueDisabled}
        >
          {actions.continueLabel}
        </button>
      )}
    </footer>
  );
}

export default function OnboardingWizard({
  stepKey,
  title,
  subtitle,
  children,
  onBack,
  onContinue,
  continueLabel = "Continue",
  continueDisabled = false,
  showSave = false,
  saveLabel = "Save",
  saveDisabled = false,
  onSave,
  contentClassName = "",
  footer,
  hideChrome = false,
}) {
  const location = useLocation();
  const { registerStepActions, clearStepActions } = useOnboardingStepActions();
  const stepIndex = getOnboardingStepIndex(stepKey);
  const activeIndex = getOnboardingStepIndexFromPath(location.pathname);
  const isActiveStep = stepIndex === activeIndex;
  const currentStep = stepIndex + 1;
  const progress = (currentStep / ONBOARDING_STEP_COUNT) * 100;

  useEffect(() => {
    if (!hideChrome || !isActiveStep) return undefined;

    registerStepActions({
      continueLabel,
      continueDisabled,
      onContinue,
      footer,
      showSave,
      saveLabel,
      saveDisabled,
      onSave,
    });

    return () => clearStepActions();
  }, [
    hideChrome,
    isActiveStep,
    continueLabel,
    continueDisabled,
    onContinue,
    footer,
    showSave,
    saveLabel,
    saveDisabled,
    onSave,
    registerStepActions,
    clearStepActions,
  ]);

  const shellClass = hideChrome ? "onboarding-wizard-step" : "onboarding-wizard";
  const innerClass = hideChrome ? "onboarding-wizard-step__inner" : "onboarding-wizard__shell";

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        {!hideChrome ? (
          <>
            <header className="onboarding-wizard__header">
              <button
                type="button"
                className="onboarding-wizard__back"
                onClick={onBack}
                aria-label="Go back"
              >
                <FiArrowLeft aria-hidden />
              </button>
              <div className="onboarding-wizard__progress-copy">
                <span className="onboarding-wizard__step-label">
                  Step {currentStep} of {ONBOARDING_STEP_COUNT}
                </span>
              </div>
            </header>

            <div className="onboarding-wizard__progress" aria-hidden>
              <div
                className="onboarding-wizard__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </>
        ) : null}

        <main className="onboarding-wizard__card">
          <div className="onboarding-wizard__card-inner">
            <h1 className="onboarding-wizard__title">{title}</h1>
            {subtitle ? <p className="onboarding-wizard__subtitle">{subtitle}</p> : null}
            <div className={`onboarding-wizard__content${contentClassName ? ` ${contentClassName}` : ""}`}>
              {children}
            </div>
          </div>
        </main>

        {!hideChrome ? (
          <footer className="onboarding-wizard__footer">
            {footer}
            <button
              type="button"
              className="onboarding-wizard__continue btn-primary"
              onClick={onContinue}
              disabled={continueDisabled}
            >
              {continueLabel}
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
